/**
 * The signed-in user's own settings (GET / PUT /api/me/settings), loaded
 * once after sign-in. Today it holds the inventory grid's column layout
 * (`grid.inventory_columns`); the later role-preset ticket builds on it.
 *
 * Per user, not per dealership: mounted above the dealership-keyed
 * DataProvider, so a super admin's layout follows them across dealerships.
 *
 * Every consumer reads the built-in defaults while the settings load, when
 * they cannot be loaded, and when the server says `can_save: false` (no
 * identity row, DISABLE_AUTH). With no provider at all (tests, public pages)
 * the defaults apply and `save` is a no-op.
 *
 * `save({ key: value | null })` applies at once and is written in the
 * background, like useUserPreferences: debounced (500 ms), a payload equal
 * to what the server holds (or is about to hold: the save in flight) is not
 * sent, a 409 refetches the version and retries once, and a failure leaves
 * the change in place for this page and raises a quiet notice (`failure`).
 * A value equal to the base is sent as null (a reset), so the server
 * keeps overrides only.
 *
 * Role presets (docs/decisions/role-presets.md): the base a key resolves
 * to without the user's own value is the role preset's value (or the
 * system default when there is no preset), so `save({ key: null })` means
 * "Reset to preset". The provider also exposes the preset in effect
 * (`preset`: { id, name, version, source }), the presets the user may
 * start from (`presetsAvailable`), the keys the user changed against the
 * preset (`customized`) and the margin block (`margin`, decision 9).
 * `choosePreset(id | null)` and `savePreferences({ 'preset.choice',
 * 'margin.floor_pct' })` write at once (after any pending change) and
 * reload the resolved settings; savePreferences rejects with the API error
 * (422: out of range) so My preferences can show it. The settings reload
 * when the dealership in view changes (`scope`).
 *
 * An admin's preset edit reaching an open session (fresh QA F-1):
 * - Sync points: `sync()` reads /api/me/settings again (preset values and
 *   version included) at most every SYNC_MIN_MS (30 s), never over unsent
 *   changes; `sync({ force: true })` skips the wait. The DataProvider calls
 *   it with every data load and forced on Refresh, and the provider itself
 *   when the tab becomes visible again: the same sync points as the
 *   dealership settings (P2-2).
 * - Every write of a key is computed against the freshest preset: the
 *   background save reads /api/me/settings right before its PUT (one GET
 *   per debounced save). If what the user started from changed meanwhile
 *   (the preset was edited, assigned or switched, or another tab saved),
 *   the user's change is re-applied to the fresh value (`KNOWN[key].rebase`:
 *   their visibility flips and, when they reordered, their order) and a
 *   reset stays a reset (null, the fresh preset). So a layout derived from
 *   a stale preset is never stored as an override. A 409 on the PUT reads
 *   again, re-applies and writes once more. Chosen over sending the preset
 *   version for a server-side 409: no backend change, and it also covers a
 *   preset assigned or switched meanwhile. The window left is the time
 *   between that GET and the PUT.
 */

import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import apiService from '../services/apiService';
import logger from '../utils/logger';
import {
    DEFAULT_USER_VALUES, GENERIC_FAILURE, KNOWN, NO_LIST, UserSettingsContext,
    customizedKeys, presetOf, resolveValue, resolveValues, same,
} from './userSettingsValue';

export { useUserSettings, StaticUserSettings } from './userSettingsValue';

export const SAVE_DEBOUNCE_MS = 500;
/** Minimum time between two sync-triggered reloads (data loads, tab visible again). */
export const SYNC_MIN_MS = 30 * 1000;
/** Kept for older imports: the visibility reload uses the same throttle. */
export const VISIBILITY_RELOAD_MS = SYNC_MIN_MS;

// The registry, defaults and context object live in userSettingsValue.js.
const DEFAULT_VALUES = DEFAULT_USER_VALUES;

/**
 * The values to write for a pending change, against the server state just
 * read (`server`: { values, base }): a reset is the fresh base; a change
 * whose starting value is still what the server holds goes as it is;
 * otherwise it is re-applied to the fresh value (`KNOWN[key].rebase`).
 */
const rebaseOnto = (server, {
    pending, from, resets, basis,
}) => Object.fromEntries(Object.keys(pending).map((key) => {
    if (resets.has(key)) return [key, server.base[key]];
    const start = from[key] ?? basis[key];
    if (same(start, server.values[key])) return [key, pending[key]];
    const rebase = KNOWN[key]?.rebase;
    return [key, rebase ? resolveValue(key, rebase(start, pending[key], server.values[key])) : pending[key]];
}));

export function UserSettingsProvider({ children, scope = null }) {
    // What the page shows: the server's values plus every local change.
    const [values, setValues] = useState(DEFAULT_VALUES);
    // What each key is without the user's own value: the preset's or the default.
    const [base, setBase] = useState(DEFAULT_VALUES);
    const [preset, setPreset] = useState(null);
    const [presetsAvailable, setPresetsAvailable] = useState(NO_LIST);
    const [margin, setMargin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [canSave, setCanSave] = useState(false);
    const [version, setVersion] = useState(0);
    const [failure, setFailure] = useState(null);

    // What the server holds: { version, values, base, canSave }.
    const serverRef = useRef({
        version: 0, values: DEFAULT_VALUES, base: DEFAULT_VALUES, canSave: false,
    });
    // Values of the save on its way to the server (compared before the
    // confirmed ones, so a revert made meanwhile is still sent).
    const inFlightRef = useRef(null);
    // The change waiting on the debounce: { key: resolved value }.
    const pendingRef = useRef(null);
    // Per pending key: the value the change started from (what the server
    // held or was about to hold), and the keys whose last change was a
    // reset. Used to re-apply the change to a fresher preset (F-1).
    const pendingFromRef = useRef({});
    const resetsRef = useRef(new Set());
    const timerRef = useRef(null);
    // Keys changed locally: a late load must not overwrite them.
    const touchedRef = useRef(new Set());
    const loadRef = useRef(null);
    const chainRef = useRef(Promise.resolve());
    const failureSeq = useRef(0);
    const mountedRef = useRef(true);

    const applyServer = useCallback((data) => {
        const can = Boolean(data?.can_save);
        const presetBase = can ? resolveValues(data?.preset?.values) : DEFAULT_VALUES;
        const resolved = can ? resolveValues(data?.values, presetBase) : DEFAULT_VALUES;
        serverRef.current = {
            version: Number.isFinite(data?.version) ? data.version : 0,
            values: resolved,
            base: presetBase,
            canSave: can,
        };
        return resolved;
    }, []);

    // Everything a GET answer carries, into state. `keep`: keys whose local
    // value stays (changed here and not yet confirmed by the server).
    const applyAll = useCallback((data, keep) => {
        const resolved = applyServer(data);
        const can = serverRef.current.canSave;
        setCanSave(can);
        setVersion(serverRef.current.version);
        setBase(serverRef.current.base);
        setPreset(can ? presetOf(data) : null);
        setPresetsAvailable(Array.isArray(data?.presets_available) ? data.presets_available : NO_LIST);
        setMargin(data?.margin && typeof data.margin === 'object' ? data.margin : null);
        setValues((prev) => {
            const next = { ...resolved };
            keep.forEach((key) => { next[key] = prev[key]; });
            return next;
        });
        setLoaded(true);
    }, [applyServer]);

    const lastLoadRef = useRef(0);
    const firstScopeRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        let cancelled = false;
        // The first load keeps every key changed meanwhile; a reload for
        // another dealership keeps only what is still waiting to be sent.
        const first = firstScopeRef.current;
        firstScopeRef.current = false;
        loadRef.current = (async () => {
            try {
                const data = await apiService.getMySettings();
                if (cancelled) return;
                lastLoadRef.current = Date.now();
                applyAll(data, first ? touchedRef.current : Object.keys(pendingRef.current || {}));
            } catch (err) {
                if (cancelled) return;
                logger.warn('User settings could not be loaded; using the defaults', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [applyAll, scope]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const fail = useCallback((keys, err) => {
        logger.warn('User settings could not be saved', err);
        if (!mountedRef.current) return;
        failureSeq.current += 1;
        const text = keys.length === 1 && KNOWN[keys[0]] ? KNOWN[keys[0]].failureText : GENERIC_FAILURE;
        setFailure({ seq: failureSeq.current, text });
    }, []);

    // A GET answer applied while a write is under way: into state, the
    // write's own values shown over it (keys changed again meanwhile keep
    // their local value).
    const showFresh = useCallback((data, target) => {
        if (!mountedRef.current) return;
        const waiting = Object.keys(pendingRef.current || {});
        applyAll(data, waiting);
        setValues((prev) => {
            const next = { ...prev };
            Object.keys(target).forEach((key) => { if (!waiting.includes(key)) next[key] = target[key]; });
            return next;
        });
    }, [applyAll]);

    const send = useCallback(async ({ pending, from, resets }) => {
        await loadRef.current;
        if (!serverRef.current.canSave) return;
        const basis = inFlightRef.current ?? serverRef.current.values;
        const changed = Object.keys(pending).filter((key) => !same(pending[key], basis[key]));
        if (!changed.length) return;
        inFlightRef.current = { ...basis, ...pending };
        let mine = inFlightRef.current;
        let target = pending;
        try {
            let result;
            for (let attempt = 0; ; attempt += 1) {
                // The freshest settings (preset included) before every write.
                // eslint-disable-next-line no-await-in-loop
                const fresh = await apiService.getMySettings();
                applyServer(fresh);
                lastLoadRef.current = Date.now();
                const server = serverRef.current;
                const next = rebaseOnto(server, { pending, from, resets, basis });
                target = next;
                inFlightRef.current = { ...server.values, ...next };
                mine = inFlightRef.current;
                showFresh(fresh, next);
                const keys = Object.keys(next).filter((key) => !same(next[key], server.values[key]));
                if (!keys.length) return;
                const body = Object.fromEntries(keys.map((key) => [
                    key, same(next[key], server.base[key]) ? null : next[key],
                ]));
                try {
                    // eslint-disable-next-line no-await-in-loop
                    result = await apiService.saveMySettings(body, server.version);
                    break;
                } catch (err) {
                    // Saved meanwhile elsewhere: read again, re-apply, write once more.
                    if (err?.status !== 409 || attempt > 0) throw err;
                }
            }
            const nextVersion = Number.isFinite(result?.version) ? result.version : serverRef.current.version + 1;
            serverRef.current = { ...serverRef.current, version: nextVersion, values: { ...serverRef.current.values, ...target } };
            if (mountedRef.current) setVersion(nextVersion);
        } catch (err) {
            fail(changed, err);
        } finally {
            if (inFlightRef.current === mine) inFlightRef.current = null;
        }
    }, [applyServer, fail, showFresh]);

    const flush = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (!pending) return chainRef.current;
        const job = {
            pending,
            from: pendingFromRef.current,
            resets: new Set([...resetsRef.current].filter((key) => key in pending)),
        };
        pendingFromRef.current = {};
        resetsRef.current = new Set();
        // One save at a time, in order: each uses the version the last left.
        chainRef.current = chainRef.current.then(() => send(job));
        return chainRef.current;
    }, [send]);

    // A save still waiting on the debounce when the provider goes (sign-out)
    // is sent rather than dropped.
    useEffect(() => () => { flush(); }, [flush]);

    const save = useCallback((patch) => {
        // null = back to the base (the preset's value, else the default).
        const resolved = Object.fromEntries(
            Object.entries(patch || {}).filter(([key]) => KNOWN[key]).map(([key, value]) => [
                key, value === null || value === undefined ? serverRef.current.base[key] : resolveValue(key, value),
            ]),
        );
        const keys = Object.keys(resolved);
        if (!keys.length) return;
        keys.forEach((key) => touchedRef.current.add(key));
        // What each change starts from, and whether it is a reset (F-1).
        const startBasis = inFlightRef.current ?? serverRef.current.values;
        keys.forEach((key) => {
            if (!(key in pendingFromRef.current)) pendingFromRef.current[key] = startBasis[key];
            if (patch[key] === null || patch[key] === undefined) resetsRef.current.add(key);
            else resetsRef.current.delete(key);
        });
        setValues((prev) => ({ ...prev, ...resolved }));
        const pending = { ...(pendingRef.current || {}), ...resolved };
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        // Nothing to send when the server holds (or is about to hold) it all.
        const basis = inFlightRef.current ?? serverRef.current.values;
        if (Object.keys(pending).every((key) => same(pending[key], basis[key]))) {
            pendingRef.current = null;
            pendingFromRef.current = {};
            resetsRef.current = new Set();
            return;
        }
        pendingRef.current = pending;
        timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    }, [flush]);

    /** GET again and apply (keys still waiting to be sent keep their local value). */
    const reload = useCallback(async () => {
        const data = await apiService.getMySettings();
        lastLoadRef.current = Date.now();
        if (mountedRef.current) applyAll(data, Object.keys(pendingRef.current || {}));
        return data;
    }, [applyAll]);

    /**
     * One explicit write (preset choice, My preferences): pending changes go
     * first, a 409 refetches the version and retries once, then the
     * resolved settings are read again. Rejects with the API error.
     */
    const commit = useCallback(async (body) => {
        await flush();
        await loadRef.current;
        if (!serverRef.current.canSave) throw new Error('Your settings cannot be saved for this sign-in.');
        const put = () => apiService.saveMySettings(body, serverRef.current.version);
        try {
            await put();
        } catch (err) {
            if (err?.status !== 409) throw err;
            applyServer(await apiService.getMySettings());
            await put();
        }
        await reload();
        return true;
    }, [flush, applyServer, reload]);

    const choosePreset = useCallback(async (presetId) => {
        try {
            return await commit({ 'preset.choice': presetId ?? null });
        } catch (err) {
            fail([], err);
            return false;
        }
    }, [commit, fail]);

    const savePreferences = useCallback((patch) => commit({ ...patch }), [commit]);

    /**
     * Read the settings again (an admin's preset edit reaches this session,
     * F-1): at most every SYNC_MIN_MS unless forced, never over unsent
     * changes or a save on its way. Resolves to the payload, or null when
     * skipped or failed.
     */
    const sync = useCallback(async ({ force = false } = {}) => {
        if (!force && Date.now() - lastLoadRef.current < SYNC_MIN_MS) return null;
        if (pendingRef.current || inFlightRef.current) return null;
        lastLoadRef.current = Date.now();
        try {
            await loadRef.current;
            if (pendingRef.current || inFlightRef.current) return null;
            return await reload();
        } catch (err) {
            logger.warn('User settings could not be reloaded', err);
            return null;
        }
    }, [reload]);

    // Back to this tab: the same sync (at most every 30 s).
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            sync();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [sync]);

    const customized = useMemo(() => {
        const keys = customizedKeys(values, base);
        return keys.length ? keys : NO_LIST;
    }, [values, base]);

    const value = useMemo(() => ({
        values,
        base,
        customized,
        preset,
        presetsAvailable,
        margin,
        loading,
        loaded,
        canSave,
        version,
        failure,
        save,
        choosePreset,
        savePreferences,
        reload,
        sync,
    }), [values, base, customized, preset, presetsAvailable, margin, loading, loaded, canSave, version, failure,
        save, choosePreset, savePreferences, reload, sync]);

    return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}

UserSettingsProvider.propTypes = { children: PropTypes.node, scope: PropTypes.string };
